import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  LayoutChangeEvent,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import Svg, { Circle, G, Line, Path, Polyline, Rect, Text as SvgText } from 'react-native-svg';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { useTheme, makeThemedStyles, DISPLAY_FONT, series, type V10Palette } from '../v10/theme';
import { Chip, GlassCard, Atmos } from '../v10/ui';
import type { ToolProps } from '../olmps/ToolHost';
import { SECTOR_LABELS, type Sector } from '../domain/olmps';
import { FloRiver } from '../olmps/FloRiver';
import { FloStatsDeck } from '../olmps/FloStatsDeck';
import { useCompassConfig } from '../olmps/sectorConfig';
import { buildRiverModel, formatNumaNote, type FloRiverList, type FloRiverTask, type FloLoadBand, type RiverItem } from '../lib/flo-weights';
import {
  computeFlow,
  greenStatus,
  calcTaskPriorityIntended as computeFloPriority,
  type FlowCompletedItem,
  type FlowCreatedItem,
  type FlowResult,
} from '../lib/flo';

// ---------------------------------------------------------------------------
// Full-fidelity native replica of the web NUMA Statistics page
// (src/app/stats/page.tsx). Sections replicated, in order:
//
//   1. Header: "Statistics" title + month navigator (‹ MMMM yyyy ›).
//   2. "Total Priority Load" card — sum of priority scores for all active
//      (not-done) tasks, shown in a circle. Uses user_settings.groups,
//      user_settings.value_settings, user_settings.unscheduled_task_priority_bonus.
//   3. "Priority Load X" card — period selector (Week/Month/Year) and chart
//      type selector (Line/Pie/Bar), rendered as native pill toggles (the
//      closest faithful phone-width equivalent of the web's <Select>
//      dropdowns). Line = cumulative priority load over time by task
//      creation date; Pie/Bar = priority load aggregated by task list.
//   4. "Protocol Activity" card — stacked success/fail bars + a pauses bar,
//      one group per protocol, for completions in the navigated month.
//      Uses `protocols` + `protocol_completions`.
//   5. Journal dropdown-prompt pie charts — one card per `dropdown`-type
//      prompt defined in user_settings.custom_journal_entry_types, showing
//      how many journal entries (this month) chose each option.
//
// All charts are hand-drawn with react-native-svg primitives (no charting
// library). Data-fetch columns verified against the live schema (snake_case
// columns, camelCase keys inside jsonb blobs), matching the web queries
// exactly (all `.eq('user_id', userId)`).
// ---------------------------------------------------------------------------

// ----- date helpers (no date-fns on mobile; replicate exact web semantics) -----

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}
function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}
function startOfWeek(d: Date): Date {
  const result = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  result.setDate(result.getDate() - result.getDay());
  return result;
}
function endOfWeek(d: Date): Date {
  const result = startOfWeek(d);
  result.setDate(result.getDate() + 6);
  return endOfDay(result);
}
function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0);
}
function endOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999);
}
function addMonths(d: Date, n: number): Date {
  const targetMonthIndex = d.getMonth() + n;
  const daysInTargetMonth = new Date(d.getFullYear(), targetMonthIndex + 1, 0).getDate();
  const day = Math.min(d.getDate(), daysInTargetMonth);
  return new Date(d.getFullYear(), targetMonthIndex, day, 0, 0, 0, 0);
}
function eachDayOfInterval(start: Date, end: Date): Date[] {
  const days: Date[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cur.getTime() <= last.getTime()) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}
function eachMonthOfInterval(start: Date, end: Date): Date[] {
  const months: Date[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur.getTime() <= last.getTime()) {
    months.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}
function isFuture(d: Date): boolean {
  return d.getTime() > Date.now();
}
function isWithinInterval(d: Date, start: Date, end: Date): boolean {
  const t = d.getTime();
  return t >= start.getTime() && t <= end.getTime();
}
function formatMonthYear(d: Date): string {
  return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}
function formatYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function formatDayNum(d: Date): string {
  return String(d.getDate());
}
function formatMonthAbbr(d: Date): string {
  return MONTH_ABBR[d.getMonth()];
}

// ----- palette (mirrors web CHART_COLORS, cycling through app tokens) -----

const CHART_COLORS: readonly string[] = series;

// ----- schema-shaped types (columns verified live, snake_case; jsonb blobs camelCase) -----

interface Criterion {
  id: string;
  title: string;
  score: number;
}
interface ValueSettingRow {
  id: string;
  isActive: boolean;
  name: string;
  criteria: Criterion[];
}
interface GroupRow {
  id: string;
  isActive: boolean;
  title: string;
  groupNumber: number;
}
interface JournalPromptRow {
  id: string;
  text: string;
  type: 'text' | 'dropdown';
  options?: string[];
}
interface CustomJournalEntryTypeRow {
  id: string;
  title: string;
  prompts: JournalPromptRow[];
}

interface SettingsState {
  groups: GroupRow[];
  valueSettings: ValueSettingRow[];
  unscheduledTaskPriorityBonus: number;
  customJournalEntryTypes: CustomJournalEntryTypeRow[];
}

const EMPTY_SETTINGS: SettingsState = {
  groups: [],
  valueSettings: [],
  // Web's in-memory default (settings-context.tsx ~line 91) is 1, not 0.
  unscheduledTaskPriorityBonus: 1,
  customJournalEntryTypes: [],
};

interface TaskRow {
  id: string;
  list_id: string;
  group_id: string | null;
  sg1_id: string | null;
  sg2_id: string | null;
  value_selections: Record<string, string> | null;
  default_priority: number | null;
  scheduled: string | null;
  category: string | null;
  created_at: string;
  // FLO River additions (Component 3) — the query below used to be
  // `.eq('done', false)`-scoped and missing these columns, which was fine
  // for the charts but incomplete for the FLO river/flow model (which needs
  // done tasks too, for Flow's completed-item derivation, plus title/
  // unlisted/tabled/updated_at). See the task-fetch effect below.
  title: string;
  done: boolean;
  unlisted: boolean;
  tabled: boolean | null;
  updated_at: string | null;
  notes: string | null;
}
interface TaskListRow {
  id: string;
  name: string;
  category_settings: Record<string, { includeInStats?: boolean }> | null;
  // FLO River addition — sector for river lanes / FloStatsDeck's sector pie.
  olmps_sector: Sector | null;
}
interface ProtocolRow {
  id: string;
  title: string;
}
interface ProtocolCompletionRow {
  id: string;
  protocol_id: string;
  protocol_title: string;
  completed_at: string | null;
  status: string;
  pause_count: number | null;
}
interface JournalEntryRow {
  id: string;
  date: string;
  entry_type_id: string;
  answers: Record<string, string> | null;
}

// ----- priority scoring -----
//
// PARITY NOTE (product decision 2026-07-03): mobile stats must show the
// SAME NUMBERS as web today, bug-for-bug. Web's stats page has a dormant
// mapping bug: calculateTaskPriorityLocal (src/app/stats/page.tsx
// ~lines 30-59) reads task.groupId / task.sg1Id / task.sg2Id /
// task.valueSelections / task.defaultPriority, but the task objects it's
// called with (src/app/stats/page.tsx ~lines 146-153) are built by
// spreading the raw snake_case Supabase row and remapping only
// id/userId/listId/createdAt/updatedAt — group_id, sg1_id, sg2_id,
// value_selections, and default_priority are never remapped to their
// camelCase counterparts. So on web those fields are always `undefined`,
// and every task's group/subgroup/value/default-priority contribution is
// silently 0. `scheduled` is unaffected (its snake_case and camelCase
// spellings are identical, so it survives the spread untouched), so the
// ONLY thing that currently affects a task's priority on web is the
// unscheduled bonus:
//
//   webEffectivePriority = task.scheduled ? 0 : unscheduledTaskPriorityBonus
//
// `calcTaskPriority` below reproduces that effective behavior exactly.
// The real/intended formula (actual group/subgroup/value/default-priority
// scoring) is preserved as `calcTaskPriorityIntended` — unused today, but
// kept so that when the web mapping bug is fixed, swapping mobile back to
// the intended formula is a one-line change (call calcTaskPriorityIntended
// instead of calcTaskPriority, once web is also fixed).
function calcTaskPriority(
  task: TaskRow,
  _groups: GroupRow[],
  _valueSettings: ValueSettingRow[],
  unscheduledBonus: number
): number {
  return task.scheduled ? 0 : unscheduledBonus;
}

// Intended formula — NOT currently called. Matches what web's
// calculateTaskPriorityLocal would compute if its mapping bug (see note
// above) were fixed. Do not wire this in without also fixing web, or the
// two platforms will diverge again.
function calcTaskPriorityIntended(
  task: TaskRow,
  groups: GroupRow[],
  valueSettings: ValueSettingRow[],
  unscheduledBonus: number
): number {
  let total = 0;
  const group = groups.find((g) => g.id === task.group_id);
  if (group) total += group.groupNumber;
  const sg1 = groups.find((g) => g.id === task.sg1_id);
  if (sg1) total += sg1.groupNumber;
  const sg2 = groups.find((g) => g.id === task.sg2_id);
  if (sg2) total += sg2.groupNumber;

  if (task.value_selections && Object.keys(task.value_selections).length > 0) {
    const activeValueSettings = valueSettings.filter((v) => v.isActive);
    for (const valueIdKey of Object.keys(task.value_selections)) {
      const criterionId = task.value_selections[valueIdKey];
      const valueSetting = activeValueSettings.find((v) => v.id === valueIdKey);
      if (valueSetting) {
        const criterion = valueSetting.criteria.find((c) => c.id === criterionId);
        if (criterion) total += criterion.score;
      }
    }
  } else if (task.default_priority !== undefined && task.default_priority !== null) {
    total = task.default_priority;
  }

  if (!task.scheduled) total += unscheduledBonus;
  return total;
}

// ----- FLO River (Component 3): flow computation helper -----
//
// Pure re-run of Flow's completed/created derivation (see flo.ts's
// getFloSnapshot) restricted to a `windowDays`-day window ending at
// `windowEndMs`. Used both for the live Flow Report card and for each point
// of the 28-day Flow Over Time series (see NumaTool's `flow`/`flowSeries`
// below). `computeFlow` itself is imported unmodified from flo.ts.
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function withinMs(ms: number, start: number, end: number): boolean {
  return ms >= start && ms <= end;
}

function computeFlowForWindow(
  tasks: FloRiverTask[],
  groups: GroupRow[],
  valueSettings: ValueSettingRow[],
  unscheduledBonus: number,
  windowEndMs: number,
  windowDays: number
): FlowResult {
  const windowStartMs = windowEndMs - windowDays * MS_PER_DAY;
  const createdItems: FlowCreatedItem[] = tasks
    .filter((t) => t.createdAt && withinMs(new Date(t.createdAt).getTime(), windowStartMs, windowEndMs))
    .map((t) => ({ id: t.id, createdAt: t.createdAt as string }));
  // `tasks` has no completed_at column, so a done task's updated_at is used
  // as a completedAt proxy — mirrors flo.ts's getFloSnapshot derivation
  // exactly (see that function's inline comment, credited here per the
  // integration spec).
  const completedItems: FlowCompletedItem[] = tasks
    .filter(
      (t) =>
        t.done && t.createdAt && t.updatedAt && withinMs(new Date(t.updatedAt).getTime(), windowStartMs, windowEndMs)
    )
    .map((t) => ({
      id: t.id,
      createdAt: t.createdAt as string,
      completedAt: t.updatedAt as string,
      priority: computeFloPriority(t, groups, valueSettings, unscheduledBonus),
      isProjectLinked: t.isProjectLinked,
    }));
  return computeFlow(completedItems, createdItems, windowDays);
}

const FLO_STATE_LABELS: Record<RiverItem['state'], string> = {
  excluded: 'Excluded',
  paused: 'Paused',
  overdue: 'Overdue',
  scheduled: 'Scheduled',
  unprocessed: 'Unprocessed',
};

function loadBandColors(t: V10Palette): Record<FloLoadBand, string> {
  return { Light: t.onPace, Normal: t.gold, Heavy: t.behind, Overloaded: t.lapsed };
}

// Ported from KyrsTool.tsx's formatScheduled (~lines 333-347).
function formatScheduled(s: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(s);
  if (!m) return s;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const label = `${MONTH_ABBR[date.getMonth()]} ${date.getDate()}`;
  if (m[4] !== undefined) {
    let hour = Number(m[4]);
    const minute = Number(m[5]);
    const suffix = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${label}, ${hour}:${String(minute).padStart(2, '0')} ${suffix}`;
  }
  return label;
}

function isSameLocalDate(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

type TimeChipKey = 'keep' | '9' | '12' | '18' | 'none';
const TIME_CHIPS: { key: TimeChipKey; label: string; minutes: number | null }[] = [
  { key: 'keep', label: 'Keep time', minutes: null },
  { key: '9', label: '9:00', minutes: 9 * 60 },
  { key: '12', label: '12:00', minutes: 12 * 60 },
  { key: '18', label: '18:00', minutes: 18 * 60 },
  { key: 'none', label: 'No time', minutes: null },
];
const WEEKDAY_ABBR = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// ----- small presentational pieces -----

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  const styles = useStyles();
  return (
    <GlassCard style={styles.card}>
      <Text style={styles.cardTitle}>{title}</Text>
      {description ? <Text style={styles.cardDescription}>{description}</Text> : null}
      <View style={styles.cardBody}>{children}</View>
    </GlassCard>
  );
}

function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  const styles = useStyles();
  return (
    <View style={styles.segmentedRow}>
      {options.map((opt) => (
        <Chip key={opt.value} small label={opt.label} active={opt.value === value} onPress={() => onChange(opt.value)} />
      ))}
    </View>
  );
}

function Legend({ items }: { items: { label: string; color: string }[] }) {
  const styles = useStyles();
  return (
    <View style={styles.legendRow}>
      {items.map((it) => (
        <View key={it.label} style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: it.color }]} />
          <Text style={styles.legendText} numberOfLines={1}>
            {it.label}
          </Text>
        </View>
      ))}
    </View>
  );
}

// Line chart: cumulative priority load over time.
function LineChartSvg({
  data,
  width,
  color,
}: {
  data: { label: string; value: number }[];
  width: number;
  color: string;
}) {
  const { t } = useTheme();
  const height = 200;
  const padLeft = 34;
  const padRight = 10;
  const padTop = 10;
  const padBottom = 22;
  const plotW = Math.max(1, width - padLeft - padRight);
  const plotH = height - padTop - padBottom;
  const maxVal = Math.max(1, ...data.map((d) => d.value));
  const xStep = data.length > 1 ? plotW / (data.length - 1) : 0;
  const points = data.map((d, i) => ({
    x: padLeft + (data.length > 1 ? i * xStep : plotW / 2),
    y: padTop + plotH - (d.value / maxVal) * plotH,
  }));
  const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(' ');
  const labelStep = Math.max(1, Math.ceil(data.length / 6));

  return (
    <Svg width={width} height={height}>
      {[0, 0.5, 1].map((frac) => {
        const y = padTop + plotH * (1 - frac);
        return (
          <G key={frac}>
            <Line
              x1={padLeft}
              y1={y}
              x2={padLeft + plotW}
              y2={y}
              stroke={t.lineFaint}
              strokeWidth={1}
            />
            <SvgText x={padLeft - 6} y={y + 3} fontSize={9} fill={t.ivorySoft} textAnchor="end">
              {Math.round(maxVal * frac)}
            </SvgText>
          </G>
        );
      })}
      <Line
        x1={padLeft}
        y1={padTop + plotH}
        x2={padLeft + plotW}
        y2={padTop + plotH}
        stroke={t.ivoryFaint}
        strokeWidth={1}
      />
      {data.length > 0 && <Polyline points={polylinePoints} fill="none" stroke={color} strokeWidth={2} />}
      {points.map((p, i) => (
        <Circle key={i} cx={p.x} cy={p.y} r={2.6} fill={color} />
      ))}
      {data.map((d, i) =>
        i % labelStep === 0 || i === data.length - 1 ? (
          <SvgText
            key={`lbl-${i}`}
            x={points[i].x}
            y={height - 6}
            fontSize={9}
            fill={t.ivorySoft}
            textAnchor="middle"
          >
            {d.label}
          </SvgText>
        ) : null
      )}
    </Svg>
  );
}

// Horizontal bar chart: priority load aggregated by task list.
function HBarChartSvg({
  data,
  width,
}: {
  data: { name: string; value: number; color: string }[];
  width: number;
}) {
  const { t } = useTheme();
  const rowHeight = 22;
  const rowGap = 10;
  const height = data.length * (rowHeight + rowGap) + rowGap;
  const labelWidth = 88;
  const valueWidth = 40;
  const barAreaWidth = Math.max(1, width - labelWidth - valueWidth);
  const maxVal = Math.max(1, ...data.map((d) => d.value));

  return (
    <Svg width={width} height={height}>
      {data.map((d, i) => {
        const y = rowGap + i * (rowHeight + rowGap);
        const barW = Math.max(2, (d.value / maxVal) * barAreaWidth);
        return (
          <G key={d.name}>
            <SvgText
              x={labelWidth - 6}
              y={y + rowHeight / 2 + 4}
              fontSize={10}
              fill={t.ivory}
              textAnchor="end"
            >
              {d.name.length > 14 ? `${d.name.slice(0, 13)}…` : d.name}
            </SvgText>
            <Rect
              x={labelWidth}
              y={y}
              width={barW}
              height={rowHeight}
              rx={4}
              fill={d.color}
            />
            <SvgText
              x={labelWidth + barW + 6}
              y={y + rowHeight / 2 + 4}
              fontSize={10}
              fontWeight="700"
              fill={t.ivory}
            >
              {d.value}
            </SvgText>
          </G>
        );
      })}
    </Svg>
  );
}

// Pie chart: priority load or journal-dropdown counts by category.
function PieChartSvg({
  data,
  width,
}: {
  data: { name: string; value: number; color: string }[];
  width: number;
}) {
  const { t } = useTheme();
  const size = Math.min(width, 220);
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 8;
  const total = data.reduce((s, d) => s + d.value, 0);

  let cursor = -Math.PI / 2;
  const slices = data.map((d) => {
    const frac = total > 0 ? d.value / total : 0;
    const angle = frac * Math.PI * 2;
    const start = cursor;
    const end = cursor + angle;
    cursor = end;
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const largeArc = angle > Math.PI ? 1 : 0;
    const path =
      total > 0 && frac > 0
        ? `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`
        : '';
    return { ...d, path, frac };
  });

  return (
    <View style={{ alignItems: 'center', gap: 10 }}>
      <Svg width={size} height={size}>
        {total <= 0 ? (
          <Circle cx={cx} cy={cy} r={r} fill={t.track} />
        ) : (
          slices.map((s, i) =>
            s.path ? <Path key={i} d={s.path} fill={s.color} /> : null
          )
        )}
      </Svg>
      <Legend items={data.map((d) => ({ label: `${d.name} (${d.value})`, color: d.color }))} />
    </View>
  );
}

// Grouped/stacked bar chart: protocol success+fail (stacked) + pauses.
function ProtocolBarChartSvg({
  data,
  width,
}: {
  data: { name: string; success: number; fail: number; pauses: number }[];
  width: number;
}) {
  const { t } = useTheme();
  const height = 200;
  const padLeft = 30;
  const padRight = 8;
  const padTop = 10;
  const padBottom = 34;
  const plotW = Math.max(1, width - padLeft - padRight);
  const plotH = height - padTop - padBottom;
  const maxVal = Math.max(1, ...data.map((d) => Math.max(d.success + d.fail, d.pauses)));
  const groupWidth = data.length > 0 ? plotW / data.length : plotW;
  const barW = Math.min(26, groupWidth * 0.32);

  return (
    <Svg width={width} height={height}>
      {[0, 0.5, 1].map((frac) => {
        const y = padTop + plotH * (1 - frac);
        return (
          <G key={frac}>
            <Line
              x1={padLeft}
              y1={y}
              x2={padLeft + plotW}
              y2={y}
              stroke={t.lineFaint}
              strokeWidth={1}
            />
            <SvgText x={padLeft - 5} y={y + 3} fontSize={9} fill={t.ivorySoft} textAnchor="end">
              {Math.round(maxVal * frac)}
            </SvgText>
          </G>
        );
      })}
      {data.map((d, i) => {
        const groupX = padLeft + i * groupWidth;
        const bar1X = groupX + groupWidth / 2 - barW - 3;
        const bar2X = groupX + groupWidth / 2 + 3;
        const bottom = padTop + plotH;
        const successH = (d.success / maxVal) * plotH;
        const failH = (d.fail / maxVal) * plotH;
        const pausesH = (d.pauses / maxVal) * plotH;
        return (
          <G key={d.name}>
            <Rect
              x={bar1X}
              y={bottom - successH}
              width={barW}
              height={successH}
              fill={t.onPace}
            />
            <Rect
              x={bar1X}
              y={bottom - successH - failH}
              width={barW}
              height={failH}
              fill={t.lapsed}
            />
            <Rect
              x={bar2X}
              y={bottom - pausesH}
              width={barW}
              height={pausesH}
              fill={series[3]}
            />
            <SvgText
              x={groupX + groupWidth / 2}
              y={height - 6}
              fontSize={8}
              fill={t.ivorySoft}
              textAnchor="middle"
            >
              {d.name.length > 10 ? `${d.name.slice(0, 9)}…` : d.name}
            </SvgText>
          </G>
        );
      })}
      <Line
        x1={padLeft}
        y1={padTop + plotH}
        x2={padLeft + plotW}
        y2={padTop + plotH}
        stroke={t.ivoryFaint}
        strokeWidth={1}
      />
    </Svg>
  );
}

// ----- FLO River (Component 3): task action sheet -----

function MiniCalendar({
  month,
  selectedDate,
  onSelectDate,
  onPrevMonth,
  onNextMonth,
}: {
  month: Date;
  selectedDate: Date | null;
  onSelectDate: (d: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}) {
  const styles = useStyles();
  const year = month.getFullYear();
  const monthIdx = month.getMonth();
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const leadBlanks = new Date(year, monthIdx, 1).getDay();
  const today = new Date();
  const cells: (Date | null)[] = [
    ...Array.from({ length: leadBlanks }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, monthIdx, i + 1)),
  ];

  return (
    <View style={styles.calendarWrap}>
      <View style={styles.calendarHeaderRow}>
        <Pressable style={styles.calendarNavBtn} onPress={onPrevMonth} hitSlop={6}>
          <Text style={styles.calendarNavText}>‹</Text>
        </Pressable>
        <Text style={styles.calendarMonthLabel}>{formatMonthYear(month)}</Text>
        <Pressable style={styles.calendarNavBtn} onPress={onNextMonth} hitSlop={6}>
          <Text style={styles.calendarNavText}>›</Text>
        </Pressable>
      </View>
      <View style={styles.calendarWeekdayRow}>
        {WEEKDAY_ABBR.map((d, i) => (
          <Text key={`wd-${i}`} style={styles.calendarWeekdayText}>
            {d}
          </Text>
        ))}
      </View>
      <View style={styles.calendarGrid}>
        {cells.map((d, i) => {
          if (!d) return <View key={`blank-${i}`} style={styles.calendarCell} />;
          const isToday = isSameLocalDate(d, today);
          const isSelected = selectedDate ? isSameLocalDate(d, selectedDate) : false;
          return (
            <Pressable key={d.getTime()} style={styles.calendarCell} onPress={() => onSelectDate(d)}>
              <View
                style={[
                  styles.calendarDayCircle,
                  isToday && styles.calendarDayToday,
                  isSelected && styles.calendarDaySelected,
                ]}
              >
                <Text style={[styles.calendarDayText, isSelected && styles.calendarDayTextSelected]}>
                  {d.getDate()}
                </Text>
              </View>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

function SheetDetailRow({ label, value }: { label: string; value: string }) {
  const styles = useStyles();
  return (
    <View style={styles.sheetDetailRow}>
      <Text style={styles.sheetDetailLabel}>{label}</Text>
      <Text style={styles.sheetDetailValue} numberOfLines={1}>
        {value}
      </Text>
    </View>
  );
}

/** NUMA's rep for a stone: append a dated line to tasks.notes (never
 *  overwrites). Returns an error message, or null on success. */
export async function appendNumaNote(userId: string, taskId: string, text: string): Promise<string | null> {
  const line = formatNumaNote(new Date(), text);
  const { data, error: readErr } = await supabase
    .from('tasks')
    .select('notes')
    .eq('id', taskId)
    .eq('user_id', userId)
    .maybeSingle();
  if (readErr) return readErr.message;
  const prev = ((data as { notes?: string | null } | null)?.notes ?? '').trimEnd();
  const { error } = await supabase
    .from('tasks')
    .update({ notes: prev ? `${prev}\n${line}` : line })
    .eq('id', taskId)
    .eq('user_id', userId);
  return error ? error.message : null;
}

export function FloTaskSheet({
  item,
  category,
  mode,
  mutating,
  errorMessage,
  onChangeMode,
  onClose,
  onMarkDone,
  onReschedule,
  onOpenKyrs,
  onSaveNote,
}: {
  item: RiverItem | null;
  category: string | null;
  mode: 'details' | 'reschedule';
  mutating: boolean;
  errorMessage: string | null;
  onChangeMode: (mode: 'details' | 'reschedule') => void;
  onClose: () => void;
  onMarkDone: (item: RiverItem) => void;
  onReschedule: (item: RiverItem, scheduledValue: string | null) => void;
  onOpenKyrs: () => void;
  /** NUMA's rep for a stone: save the "what are you doing about it?" line. */
  onSaveNote?: (item: RiverItem, text: string) => void;
}) {
  const styles = useStyles();
  const [noteText, setNoteText] = useState('');
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => item?.scheduledAt ?? new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(item?.scheduledAt ?? null);
  const [timeChip, setTimeChip] = useState<TimeChipKey>('none');

  useEffect(() => {
    if (!item) return;
    const hasTime = !!item.scheduledRaw && /\d{2}:\d{2}/.test(item.scheduledRaw);
    setCalendarMonth(item.scheduledAt ?? new Date());
    setSelectedDate(item.scheduledAt ?? null);
    setTimeChip(hasTime ? 'keep' : 'none');
    setNoteText('');
  }, [item]);

  if (!item) return null;

  const hasExistingTime = !!item.scheduledRaw && /\d{2}:\d{2}/.test(item.scheduledRaw);

  const save = () => {
    const date = selectedDate ?? new Date();
    let minutes: number | null;
    if (timeChip === 'keep') {
      const m = item.scheduledRaw ? /(\d{2}):(\d{2})/.exec(item.scheduledRaw) : null;
      minutes = m ? Number(m[1]) * 60 + Number(m[2]) : null;
    } else {
      minutes = TIME_CHIPS.find((c) => c.key === timeChip)?.minutes ?? null;
    }
    const ymd = formatYMD(date);
    const scheduledValue =
      minutes === null
        ? ymd
        : `${ymd} ${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
    onReschedule(item, scheduledValue);
  };

  return (
    <Modal visible={!!item} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.sheetBackdrop} onPress={onClose}>
        <Pressable onPress={(e) => e.stopPropagation()} style={styles.sheetWrap}>
        <GlassCard strong style={styles.sheetCard}>
          {mode === 'details' ? (
            <>
              <Text style={styles.sheetTitle} numberOfLines={2}>
                {item.title}
              </Text>
              <Text style={styles.sheetSubline}>
                {item.listName}
                {category ? ` · ${category}` : ''}
              </Text>
              <View style={styles.sheetDetailRows}>
                <SheetDetailRow
                  label="Sector"
                  value={item.sector ? `${item.sector} · ${SECTOR_LABELS[item.sector]}` : '—'}
                />
                <SheetDetailRow
                  label="Scheduled"
                  value={item.scheduledRaw ? formatScheduled(item.scheduledRaw) : 'Unscheduled'}
                />
                <SheetDetailRow label="Raw priority" value={item.rawPriority.toFixed(2)} />
                <SheetDetailRow label="State" value={`${FLO_STATE_LABELS[item.state]} ×${item.stateModifier}`} />
                <SheetDetailRow label="Weight" value={item.weight.toFixed(2)} />
                {item.daysOverdue > 0 ? (
                  <SheetDetailRow
                    label="Overdue"
                    value={`${item.daysOverdue} day${item.daysOverdue === 1 ? '' : 's'} late`}
                  />
                ) : null}
              </View>
              {item.isStone && onSaveNote ? (
                <View style={styles.noteBlock}>
                  <Text style={styles.noteLabel}>What are you doing about it?</Text>
                  {item.lastNote ? <Text style={styles.noteLast}>Last: {item.lastNote}</Text> : null}
                  <TextInput
                    style={styles.noteInput}
                    value={noteText}
                    onChangeText={setNoteText}
                    placeholder="One line: reschedule, redistribute, or act"
                    placeholderTextColor="#7a8494"
                    maxLength={200}
                    editable={!mutating}
                    returnKeyType="done"
                    onSubmitEditing={() => noteText.trim() && onSaveNote(item, noteText)}
                  />
                  <Pressable
                    style={[styles.sheetBtn, styles.sheetBtnPrimary, !noteText.trim() && { opacity: 0.5 }]}
                    disabled={mutating || !noteText.trim()}
                    onPress={() => onSaveNote(item, noteText)}
                  >
                    <Text style={[styles.sheetBtnText, styles.sheetBtnPrimaryText]}>Save line (NUMA rep)</Text>
                  </Pressable>
                </View>
              ) : null}
              {errorMessage ? <Text style={styles.sheetError}>{errorMessage}</Text> : null}
              <View style={styles.sheetButtonCol}>
                <Pressable
                  style={[styles.sheetBtn, styles.sheetBtnPrimary]}
                  disabled={mutating}
                  onPress={() => onMarkDone(item)}
                >
                  <Text style={[styles.sheetBtnText, styles.sheetBtnPrimaryText]}>Mark done</Text>
                </Pressable>
                <Pressable style={styles.sheetBtn} disabled={mutating} onPress={() => onChangeMode('reschedule')}>
                  <Text style={styles.sheetBtnText}>Reschedule</Text>
                </Pressable>
                <Pressable style={styles.sheetBtn} disabled={mutating} onPress={onOpenKyrs}>
                  <Text style={styles.sheetBtnText}>Open in KYRS</Text>
                </Pressable>
                <Pressable style={styles.sheetBtn} disabled={mutating} onPress={onClose}>
                  <Text style={styles.sheetBtnText}>Cancel</Text>
                </Pressable>
              </View>
            </>
          ) : (
            <>
              <Text style={styles.sheetTitle} numberOfLines={2}>
                {`Reschedule "${item.title}"`}
              </Text>
              <MiniCalendar
                month={calendarMonth}
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
                onPrevMonth={() => setCalendarMonth((m) => addMonths(m, -1))}
                onNextMonth={() => setCalendarMonth((m) => addMonths(m, 1))}
              />
              <View style={styles.chipWrapRow}>
                {TIME_CHIPS.filter((c) => c.key !== 'keep' || hasExistingTime).map((c) => (
                  <Pressable
                    key={c.key}
                    style={[styles.timeChip, timeChip === c.key && styles.timeChipActive]}
                    onPress={() => setTimeChip(c.key)}
                  >
                    <Text style={[styles.timeChipText, timeChip === c.key && styles.timeChipTextActive]}>
                      {c.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
              {errorMessage ? <Text style={styles.sheetError}>{errorMessage}</Text> : null}
              <View style={styles.sheetButtonRow}>
                <Pressable style={styles.sheetBtn} disabled={mutating} onPress={() => onChangeMode('details')}>
                  <Text style={styles.sheetBtnText}>Back</Text>
                </Pressable>
                <Pressable style={[styles.sheetBtn, styles.sheetBtnPrimary]} disabled={mutating} onPress={save}>
                  <Text style={[styles.sheetBtnText, styles.sheetBtnPrimaryText]}>Save</Text>
                </Pressable>
              </View>
            </>
          )}
        </GlassCard>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ----- main tool -----

type LoadChartType = 'line' | 'pie' | 'bar';
type LoadChartPeriod = 'week' | 'month' | 'year';

export function NumaTool({ onOpenTool }: ToolProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;
  const { config: compassConfig } = useCompassConfig(userId);
  const greenBands = compassConfig.game.greenBands;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [settings, setSettings] = useState<SettingsState>(EMPTY_SETTINGS);
  const [journalEntries, setJournalEntries] = useState<JournalEntryRow[]>([]);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [taskLists, setTaskLists] = useState<TaskListRow[]>([]);
  const [protocols, setProtocols] = useState<ProtocolRow[]>([]);
  const [protocolLogs, setProtocolLogs] = useState<ProtocolCompletionRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [loadChartType, setLoadChartType] = useState<LoadChartType>('line');
  const [loadChartPeriod, setLoadChartPeriod] = useState<LoadChartPeriod>('month');

  // FLO River (Component 3) additions.
  const [riverView, setRiverView] = useState<'weight' | 'sector'>('weight');
  // Bumped after a sheet mutation (Mark done / Reschedule) to re-run the
  // fetch effect below without touching its existing userId/currentDate
  // triggers — reuses the exact same fetch path so the river, FloStatsDeck,
  // and every pre-existing chart all refresh together.
  const [refreshTick, setRefreshTick] = useState(0);
  const [sheetItem, setSheetItem] = useState<RiverItem | null>(null);
  const [sheetMode, setSheetMode] = useState<'details' | 'reschedule'>('details');
  const [sheetMutating, setSheetMutating] = useState(false);
  const [sheetError, setSheetError] = useState<string | null>(null);

  const [panelWidth, setPanelWidth] = useState(280);
  const onRootLayout = useCallback((e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width;
    if (w > 0) setPanelWidth(w);
  }, []);
  const chartWidth = Math.max(180, panelWidth - 24 - 28);

  // Settings (groups / value settings / bonus / journal prompt definitions).
  useEffect(() => {
    if (!userId) return;
    (async () => {
      const { data, error: err } = await supabase
        .from('user_settings')
        .select('groups, value_settings, unscheduled_task_priority_bonus, custom_journal_entry_types')
        .eq('user_id', userId)
        .maybeSingle();
      if (err) {
        setError(err.message);
        return;
      }
      if (data) {
        setSettings({
          groups: (data.groups ?? []) as GroupRow[],
          valueSettings: (data.value_settings ?? []) as ValueSettingRow[],
          unscheduledTaskPriorityBonus: Number(data.unscheduled_task_priority_bonus ?? 0),
          customJournalEntryTypes: (data.custom_journal_entry_types ?? []) as CustomJournalEntryTypeRow[],
        });
      }
    })();
  }, [userId]);

  // Month-scoped data (journal entries) + full-set data (tasks/lists/protocols/logs).
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const from = startOfMonth(currentDate);
      const to = endOfMonth(currentDate);
      try {
        const [journalResult, tasksResult, listsResult, protocolsResult, logsResult] = await Promise.all([
          supabase
            .from('journal_entries')
            .select('id, date, entry_type_id, answers')
            .eq('user_id', userId)
            .gte('date', formatYMD(from))
            .lte('date', formatYMD(to)),
          supabase
            .from('tasks')
            // FLO River (Component 3): no `.eq('done', false)` filter — the
            // river/flow model needs ALL of the user's tasks (done tasks
            // feed Flow's completed-item derivation; unlisted/tabled feed
            // FLO v3.0 state inference). `activeTasks` below now filters
            // `!t.done` itself, replacing what this query used to do.
            .select(
              'id, list_id, group_id, sg1_id, sg2_id, value_selections, default_priority, scheduled, category, created_at, title, done, unlisted, tabled, updated_at, notes'
            )
            .eq('user_id', userId),
          supabase
            .from('task_lists')
            .select('id, name, category_settings, olmps_sector')
            .eq('user_id', userId),
          supabase
            .from('protocols')
            .select('id, title')
            .eq('user_id', userId),
          supabase
            .from('protocol_completions')
            .select('id, protocol_id, protocol_title, completed_at, status, pause_count')
            .eq('user_id', userId),
        ]);
        if (cancelled) return;

        const firstError =
          journalResult.error || tasksResult.error || listsResult.error || protocolsResult.error || logsResult.error;
        if (firstError) {
          setError(firstError.message);
        } else {
          setError(null);
        }

        setJournalEntries((journalResult.data ?? []) as JournalEntryRow[]);
        setTasks((tasksResult.data ?? []) as TaskRow[]);
        setTaskLists((listsResult.data ?? []) as TaskListRow[]);
        setProtocols((protocolsResult.data ?? []) as ProtocolRow[]);
        setProtocolLogs((logsResult.data ?? []) as ProtocolCompletionRow[]);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, currentDate, refreshTick]);

  // activeTasks: not-done tasks, excluding categories flagged includeInStats === false.
  const activeTasks = useMemo(() => {
    const catByList = new Map(taskLists.map((l) => [l.id, l.category_settings ?? {}]));
    return tasks.filter((t) => {
      // The task query no longer pre-filters `done` (see FLO River comment
      // on the fetch above) — replicate the old `.eq('done', false)` here.
      if (t.done) return false;
      if (t.category) {
        const cat = catByList.get(t.list_id);
        if (cat && cat[t.category]?.includeInStats === false) return false;
      }
      return true;
    });
  }, [tasks, taskLists]);

  const totalPriorityLoad = useMemo(() => {
    return activeTasks.reduce(
      (sum, t) => sum + calcTaskPriority(t, settings.groups, settings.valueSettings, settings.unscheduledTaskPriorityBonus),
      0
    );
  }, [activeTasks, settings]);

  const priorityLoadChartData = useMemo(() => {
    if (!activeTasks.length) return { kind: 'empty' as const };

    if (loadChartType === 'pie' || loadChartType === 'bar') {
      const byList = new Map<string, number>();
      for (const t of activeTasks) {
        const list = taskLists.find((l) => l.id === t.list_id);
        const name = list?.name || 'Unknown List';
        const priority = calcTaskPriority(t, settings.groups, settings.valueSettings, settings.unscheduledTaskPriorityBonus);
        byList.set(name, (byList.get(name) ?? 0) + priority);
      }
      const data = Array.from(byList.entries()).map(([name, value], i) => ({
        name,
        value,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }));
      return { kind: 'categorical' as const, data };
    }

    // line: cumulative priority load by task creation date, over the period.
    const now = new Date();
    let interval: { start: Date; end: Date };
    if (loadChartPeriod === 'week') interval = { start: startOfWeek(now), end: endOfWeek(now) };
    else if (loadChartPeriod === 'month') interval = { start: startOfMonth(now), end: endOfMonth(now) };
    else interval = { start: startOfYear(now), end: endOfYear(now) };

    const timeIntervals =
      loadChartPeriod === 'year'
        ? eachMonthOfInterval(interval.start, interval.end).filter((m) => !isFuture(m))
        : eachDayOfInterval(interval.start, interval.end).filter((d) => !isFuture(d));

    const points = timeIntervals.map((date) => {
      const endOfPeriod = loadChartPeriod === 'year' ? endOfMonth(date) : endOfDay(date);
      const eligible = activeTasks.filter((t) => {
        const createdAt = t.created_at ? new Date(t.created_at).getTime() : 0;
        return createdAt > 0 && createdAt <= endOfPeriod.getTime();
      });
      const value = eligible.reduce(
        (sum, t) => sum + calcTaskPriority(t, settings.groups, settings.valueSettings, settings.unscheduledTaskPriorityBonus),
        0
      );
      const label = loadChartPeriod === 'year' ? formatMonthAbbr(date) : formatDayNum(date);
      return { label, value };
    });

    return { kind: 'line' as const, data: points };
  }, [activeTasks, taskLists, settings, loadChartType, loadChartPeriod]);

  const protocolActivityData = useMemo(() => {
    const from = startOfMonth(currentDate);
    const to = endOfMonth(currentDate);
    const logsInPeriod = protocolLogs.filter((log) => {
      const completed = log.completed_at ? new Date(log.completed_at) : new Date(0);
      return isWithinInterval(completed, from, to);
    });

    const success: Record<string, number> = {};
    const fail: Record<string, number> = {};
    const pauses: Record<string, number> = {};
    const titles: Record<string, string> = {};

    for (const p of protocols) {
      titles[p.id] = p.title;
      success[p.id] = 0;
      fail[p.id] = 0;
      pauses[p.id] = 0;
    }
    for (const log of logsInPeriod) {
      if (log.status === 'success') success[log.protocol_id] = (success[log.protocol_id] ?? 0) + 1;
      else fail[log.protocol_id] = (fail[log.protocol_id] ?? 0) + 1;
      pauses[log.protocol_id] = (pauses[log.protocol_id] ?? 0) + (log.pause_count ?? 0);
      if (!titles[log.protocol_id]) titles[log.protocol_id] = log.protocol_title;
    }

    return Object.keys(titles)
      .map((id) => ({
        name: titles[id],
        success: success[id] ?? 0,
        fail: fail[id] ?? 0,
        pauses: pauses[id] ?? 0,
      }))
      .filter((d) => d.success > 0 || d.fail > 0 || d.pauses > 0);
  }, [protocolLogs, protocols, currentDate]);

  const journalChartsData = useMemo(() => {
    if (!journalEntries.length || !settings.customJournalEntryTypes.length) return [];
    const dropdownPrompts = settings.customJournalEntryTypes.flatMap((type) =>
      (type.prompts || [])
        .filter((p) => p.type === 'dropdown')
        .map((p) => ({ ...p, typeTitle: type.title }))
    );

    const charts = dropdownPrompts.map((prompt) => {
      const counts: Record<string, number> = {};
      (prompt.options || []).forEach((opt) => {
        counts[opt] = 0;
      });
      for (const entry of journalEntries) {
        const answer = entry.answers?.[prompt.id];
        if (answer && Object.prototype.hasOwnProperty.call(counts, answer)) counts[answer]++;
      }
      const data = Object.entries(counts).map(([name, value], i) => {
        let color = CHART_COLORS[i % CHART_COLORS.length];
        if (name.toLowerCase() === 'yes') color = t.onPace;
        if (name.toLowerCase() === 'no') color = t.lapsed;
        return { name, value, color };
      });
      return { promptId: prompt.id, promptText: prompt.text, typeTitle: prompt.typeTitle, data };
    });

    return charts.filter((c) => c.data.some((d) => d.value > 0));
  }, [journalEntries, settings.customJournalEntryTypes, t]);

  // ------------------------------------------------------------------------
  // FLO River (Component 3): data wiring, flow computation, sheet handlers.
  // See scratchpad/FLO_RIVER_SPEC.md, "Component 3", and flo-weights.ts for
  // the model this feeds.
  // ------------------------------------------------------------------------

  // Row → FloRiverTask. buildRiverModel/inferState only read
  // done/unlisted/tabled/scheduled/id/title/listId plus the priority-scoring
  // fields (groupId/sg1Id/sg2Id/valueSelections/defaultPriority/scheduled);
  // `etc`/`isProjectLinked` aren't read by buildRiverModel or by the Flow
  // derivation below, so they're stubbed here (see FloTaskLike/FloRiverTask
  // in flo-weights.ts for the type's full field list).
  const floTasks: FloRiverTask[] = useMemo(
    () =>
      tasks.map((t) => ({
        id: t.id,
        title: t.title ?? '',
        listId: t.list_id,
        category: t.category,
        groupId: t.group_id,
        sg1Id: t.sg1_id,
        sg2Id: t.sg2_id,
        valueSelections: t.value_selections,
        defaultPriority: t.default_priority,
        etc: null,
        scheduled: t.scheduled,
        done: !!t.done,
        unlisted: !!t.unlisted,
        createdAt: t.created_at,
        updatedAt: t.updated_at,
        isProjectLinked: false,
        tabled: !!t.tabled,
        notes: t.notes ?? null,
      })),
    [tasks]
  );

  const floLists: FloRiverList[] = useMemo(
    () => taskLists.map((l) => ({ id: l.id, name: l.name, sector: l.olmps_sector ?? null })),
    [taskLists]
  );

  // Sheet-only lookup: RiverItem doesn't carry `category` (see flo-weights.ts)
  // — the action sheet's "list name · category" detail line reads it from
  // the raw task row instead.
  const taskCategoryById = useMemo(() => new Map(tasks.map((t) => [t.id, t.category])), [tasks]);

  const riverModel = useMemo(
    () =>
      buildRiverModel(
        floTasks,
        floLists,
        settings.groups,
        settings.valueSettings,
        settings.unscheduledTaskPriorityBonus
      ),
    [floTasks, floLists, settings]
  );

  const FLOW_WINDOW_DAYS = 28;

  // Green band (Book 2.3): Load is green at or below baseline x factor. Charge
  // is not computed on this screen; Flow's band is applied in FloStatsDeck.
  const loadGreen = greenStatus(
    { charge: Infinity, load: riverModel.totalLoad, flow: Infinity },
    greenBands,
    riverModel.baseline
  ).load;

  const flow: FlowResult = useMemo(
    () =>
      computeFlowForWindow(
        floTasks,
        settings.groups,
        settings.valueSettings,
        settings.unscheduledTaskPriorityBonus,
        Date.now(),
        FLOW_WINDOW_DAYS
      ),
    [floTasks, settings]
  );

  const flowSeries = useMemo(() => {
    const series: { date: string; score: number }[] = [];
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    for (let i = FLOW_WINDOW_DAYS - 1; i >= 0; i--) {
      const dayStart = new Date(todayStart.getTime() - i * MS_PER_DAY);
      // Window ends at this day's last millisecond ("28-day window ending
      // that day"). NOTE: computeFlow's own Steadiness sub-metric measures
      // daily counts back from the real `Date.now()` — it has no "as-of"
      // parameter and flo.ts is frozen, not modified here — so Steadiness on
      // historical points in this series is approximate; Cycle/Exchange/
      // Latency are exact for the filtered window.
      const dayEndMs = dayStart.getTime() + MS_PER_DAY - 1;
      const result = computeFlowForWindow(
        floTasks,
        settings.groups,
        settings.valueSettings,
        settings.unscheduledTaskPriorityBonus,
        dayEndMs,
        FLOW_WINDOW_DAYS
      );
      series.push({ date: formatYMD(dayStart), score: result.score });
    }
    return series;
  }, [floTasks, settings]);

  const openItemSheet = useCallback((item: RiverItem) => {
    setSheetItem(item);
    setSheetMode('details');
    setSheetError(null);
  }, []);

  const closeItemSheet = useCallback(() => {
    setSheetItem(null);
    setSheetMode('details');
    setSheetError(null);
  }, []);

  const markItemDone = useCallback(
    async (item: RiverItem) => {
      if (!userId) return;
      setSheetMutating(true);
      setSheetError(null);
      // KYRS's own toggleDone (KyrsTool.tsx ~2122-2137) re-runs the list's
      // auto-scheduler after this mutation (triggerListReschedule) — that
      // internal trigger is NOT replicated here; this is a direct write.
      const { error: err } = await supabase
        .from('tasks')
        .update({ done: true, updated_at: new Date().toISOString(), scheduled_manual: false })
        .eq('id', item.id)
        .eq('user_id', userId);
      setSheetMutating(false);
      if (err) {
        setSheetError(err.message);
        return;
      }
      closeItemSheet();
      setRefreshTick((t) => t + 1);
    },
    [userId, closeItemSheet]
  );

  const rescheduleItem = useCallback(
    async (item: RiverItem, scheduledValue: string | null) => {
      if (!userId) return;
      setSheetMutating(true);
      setSheetError(null);
      const { error: err } = await supabase
        .from('tasks')
        .update({ scheduled: scheduledValue, scheduled_manual: true, updated_at: new Date().toISOString() })
        .eq('id', item.id)
        .eq('user_id', userId);
      setSheetMutating(false);
      if (err) {
        setSheetError(err.message);
        return;
      }
      closeItemSheet();
      setRefreshTick((t) => t + 1);
    },
    [userId, closeItemSheet]
  );

  const saveStoneNote = useCallback(
    async (item: RiverItem, text: string) => {
      if (!userId) return;
      setSheetMutating(true);
      setSheetError(null);
      const err = await appendNumaNote(userId, item.id, text);
      setSheetMutating(false);
      if (err) {
        setSheetError(err);
        return;
      }
      closeItemSheet();
      setRefreshTick((t) => t + 1);
    },
    [userId, closeItemSheet]
  );

  const openInKyrs = useCallback(() => {
    onOpenTool?.('kyrs', sheetItem ? { initialTaskId: sheetItem.id } : undefined);
    closeItemSheet();
  }, [onOpenTool, closeItemSheet, sheetItem]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate((prev) => addMonths(prev, direction === 'prev' ? -1 : 1));
  };

  if (loading && !journalEntries.length && !tasks.length) {
    return (
      <Atmos style={styles.center}>
        <ActivityIndicator color={t.goldBright} />
      </Atmos>
    );
  }

  return (
    <Atmos>
    <ScrollView style={styles.root} contentContainerStyle={styles.content} onLayout={onRootLayout}>
      <GlassCard style={styles.heroCard}>
        <View style={styles.heroHeaderRow}>
          <Text style={styles.heroTitle}>FLO River</Text>
          <Segmented
            options={[
              { value: 'weight', label: 'Weight' },
              { value: 'sector', label: 'Sector' },
            ]}
            value={riverView}
            onChange={setRiverView}
          />
        </View>
        <FloRiver model={riverModel} view={riverView} onPressItem={openItemSheet} />
        <View style={styles.loadReadoutRow}>
          <Text style={styles.loadReadoutValue}>{riverModel.totalLoad.toFixed(1)}</Text>
          <View
            style={[
              styles.loadBandChip,
              { backgroundColor: loadGreen ? t.gold : loadBandColors(t)[riverModel.loadBand] === t.gold ? t.behind : loadBandColors(t)[riverModel.loadBand] },
            ]}
          >
            <Text style={styles.loadBandChipText}>{loadGreen ? 'Green band' : 'Over green band'}</Text>
          </View>
        </View>
      </GlassCard>

      <FloStatsDeck model={riverModel} flow={flow} flowSeries={flowSeries} flowMin={greenBands.flowMin} />

      <FloTaskSheet
        item={sheetItem}
        category={sheetItem ? taskCategoryById.get(sheetItem.id) ?? null : null}
        mode={sheetMode}
        mutating={sheetMutating}
        errorMessage={sheetError}
        onChangeMode={setSheetMode}
        onClose={closeItemSheet}
        onMarkDone={markItemDone}
        onReschedule={rescheduleItem}
        onOpenKyrs={openInKyrs}
        onSaveNote={saveStoneNote}
      />

      <View style={styles.header}>
        <Text style={styles.title}>Statistics</Text>
        <View style={styles.monthNav}>
          <Pressable style={styles.navButton} onPress={() => navigateMonth('prev')}>
            <Text style={styles.navButtonText}>‹</Text>
          </Pressable>
          <Text style={styles.monthLabel}>{formatMonthYear(currentDate)}</Text>
          <Pressable style={styles.navButton} onPress={() => navigateMonth('next')}>
            <Text style={styles.navButtonText}>›</Text>
          </Pressable>
        </View>
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      <SectionCard title="Total Priority Load" description="Sum of priority scores for all active tasks.">
        <View style={styles.loadCircleWrap}>
          <GlassCard style={styles.loadCircle}>
            <Text style={styles.loadCircleValue}>{totalPriorityLoad}</Text>
          </GlassCard>
        </View>
      </SectionCard>

      <SectionCard title="Priority Load X" description="Visualize your task priority over time and by list.">
        <View style={styles.selectorsRow}>
          <Segmented
            options={[
              { value: 'week', label: 'This Week' },
              { value: 'month', label: 'This Month' },
              { value: 'year', label: 'This Year' },
            ]}
            value={loadChartPeriod}
            onChange={setLoadChartPeriod}
          />
          <Segmented
            options={[
              { value: 'line', label: 'Line Chart' },
              { value: 'pie', label: 'Pie Chart' },
              { value: 'bar', label: 'Bar Chart' },
            ]}
            value={loadChartType}
            onChange={setLoadChartType}
          />
        </View>
        {priorityLoadChartData.kind === 'line' && priorityLoadChartData.data.length > 0 ? (
          <LineChartSvg data={priorityLoadChartData.data} width={chartWidth} color={t.gold} />
        ) : null}
        {priorityLoadChartData.kind === 'categorical' && loadChartType === 'bar' && priorityLoadChartData.data.length > 0 ? (
          <HBarChartSvg data={priorityLoadChartData.data} width={chartWidth} />
        ) : null}
        {priorityLoadChartData.kind === 'categorical' && loadChartType === 'pie' && priorityLoadChartData.data.length > 0 ? (
          <PieChartSvg data={priorityLoadChartData.data} width={chartWidth} />
        ) : null}
        {priorityLoadChartData.kind === 'empty' ? (
          <Text style={styles.empty}>No priority data to display for the selected period/type.</Text>
        ) : null}
      </SectionCard>

      <SectionCard
        title="Protocol Activity"
        description="Completions and pauses for each protocol this month."
      >
        {protocolActivityData.length > 0 ? (
          <>
            <ProtocolBarChartSvg data={protocolActivityData} width={chartWidth} />
            <Legend
              items={[
                { label: 'Success', color: t.onPace },
                { label: 'Fail', color: t.lapsed },
                { label: 'Pauses', color: series[3] },
              ]}
            />
          </>
        ) : (
          <Text style={styles.empty}>No protocol completion data for {formatMonthYear(currentDate)}.</Text>
        )}
      </SectionCard>

      {loading ? (
        <Text style={styles.loadingLine}>Loading journal stats for {formatMonthYear(currentDate)}...</Text>
      ) : journalChartsData.length > 0 ? (
        journalChartsData.map((chart) => (
          <SectionCard
            key={chart.promptId}
            title={chart.promptText}
            description={`From journal: ${chart.typeTitle}`}
          >
            <PieChartSvg data={chart.data} width={chartWidth} />
          </SectionCard>
        ))
      ) : (
        <Text style={styles.empty}>
          No data found for journal dropdown prompts in {formatMonthYear(currentDate)}.
        </Text>
      )}
    </ScrollView>
    </Atmos>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
  root: { flex: 1, backgroundColor: 'transparent' },
  content: { padding: 12, gap: 12, paddingBottom: 32 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: 20, color: t.ivory, fontFamily: DISPLAY_FONT, letterSpacing: 3, textTransform: 'uppercase' },
  monthNav: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  navButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.navyRaised,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  navButtonText: { fontSize: 16, fontWeight: '900', color: t.goldBright },
  monthLabel: { fontSize: 13, fontWeight: '700', color: t.ivory, width: 108, textAlign: 'center' },
  error: { color: t.lapsed, fontSize: 13 },
  card: {
    padding: 16,
    gap: 4,
  },
  cardTitle: { fontSize: 14, color: t.ivory, fontFamily: DISPLAY_FONT, letterSpacing: 2.5, textTransform: 'uppercase' },
  cardDescription: { fontSize: 12, color: t.ivorySoft },
  cardBody: { marginTop: 8, alignItems: 'center' },
  loadCircleWrap: { paddingVertical: 8 },
  loadCircle: {
    width: 148,
    height: 148,
    borderRadius: 74,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: t.gold,
    boxShadow: `0 0 24px ${t.goldGlow}`,
  } as object,
  loadCircleValue: { fontSize: 38, fontWeight: '900', color: t.goldBright },
  selectorsRow: { width: '100%', gap: 8, marginBottom: 4 },
  segmentedRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  legendRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 4 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 6, maxWidth: 140 },
  legendDot: { width: 10, height: 10, borderRadius: 5 },
  legendText: { fontSize: 11, color: t.ivory },
  empty: { color: t.ivorySoft, fontStyle: 'italic', textAlign: 'center', paddingVertical: 20, fontSize: 12 },
  loadingLine: { color: t.ivorySoft, fontStyle: 'italic', textAlign: 'center', paddingVertical: 12, fontSize: 12 },

  // FLO River (Component 3) — hero card + load readout.
  heroCard: {
    padding: 16,
    gap: 10,
  },
  heroHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  heroTitle: { fontSize: 14, color: t.ivory, fontFamily: DISPLAY_FONT, letterSpacing: 2.5, textTransform: 'uppercase' },
  loadReadoutRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 },
  loadReadoutValue: { fontSize: 30, fontWeight: '900', color: t.goldBright },
  loadBandChip: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 999 },
  noteBlock: { marginTop: 10, gap: 6 },
  noteLabel: { fontSize: 12, fontWeight: '800', color: t.goldBright },
  noteLast: { fontSize: 11, color: t.ivorySoft, fontStyle: 'italic' },
  noteInput: {
    backgroundColor: '#ffffff',
    color: '#1a1f2b',
    borderWidth: 1.5,
    borderColor: t.navyLine,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 14,
  },
  loadBandChipText: { fontSize: 12, fontWeight: '800', color: t.onGold },

  // FLO River (Component 3) — task action sheet.
  sheetBackdrop: { flex: 1, backgroundColor: 'rgba(2,4,10,0.6)', alignItems: 'center', justifyContent: 'center' },
  sheetWrap: { width: '86%', maxWidth: 380 },
  sheetCard: {
    padding: 18,
    boxShadow: `0 10px 30px ${t.glowSoft}`,
  } as object,
  sheetTitle: { fontSize: 16, color: t.ivory, fontFamily: DISPLAY_FONT, letterSpacing: 2, textTransform: 'uppercase' },
  sheetSubline: { fontSize: 12, color: t.ivorySoft, marginTop: 2, marginBottom: 10 },
  sheetDetailRows: { gap: 6 },
  sheetDetailRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 8 },
  sheetDetailLabel: { fontSize: 12, color: t.ivorySoft, fontWeight: '600' },
  sheetDetailValue: { fontSize: 12, color: t.ivory, fontWeight: '700', flexShrink: 1, textAlign: 'right' },
  sheetError: { color: t.lapsed, fontSize: 12, marginTop: 8 },
  sheetButtonCol: { gap: 8, marginTop: 16 },
  sheetButtonRow: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  sheetBtn: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.navyLine,
    alignItems: 'center',
  },
  sheetBtnPrimary: { backgroundColor: t.gold, borderColor: t.goldBright, boxShadow: `0 6px 18px ${t.goldGlow}` } as object,
  sheetBtnText: { fontSize: 13, fontWeight: '800', color: t.ivorySoft },
  sheetBtnPrimaryText: { color: t.onGold },

  // Mini reschedule calendar.
  calendarWrap: { gap: 8, marginTop: 6 },
  calendarHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  calendarNavBtn: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  calendarNavText: { fontSize: 15, fontWeight: '900', color: t.goldBright },
  calendarMonthLabel: { fontSize: 13, fontWeight: '800', color: t.ivory },
  calendarWeekdayRow: { flexDirection: 'row' },
  calendarWeekdayText: { flex: 1, textAlign: 'center', fontSize: 10, fontWeight: '700', color: t.ivorySoft },
  calendarGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  calendarCell: { width: '14.28%', aspectRatio: 1, alignItems: 'center', justifyContent: 'center' },
  calendarDayCircle: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  calendarDayToday: { borderWidth: 1, borderColor: t.gold },
  calendarDaySelected: { backgroundColor: t.gold, boxShadow: `0 0 12px ${t.goldGlow}` } as object,
  calendarDayText: { fontSize: 12, fontWeight: '600', color: t.ivory },
  calendarDayTextSelected: { color: t.onGold, fontWeight: '900' },

  // Reschedule time chips.
  chipWrapRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  timeChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  timeChipActive: { backgroundColor: t.gold, borderColor: t.goldBright, boxShadow: `0 0 12px ${t.goldGlow}` } as object,
  timeChipText: { fontSize: 11, fontWeight: '700', color: t.ivorySoft },
  timeChipTextActive: { color: t.onGold },
}),
);
